import { Component, effect, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { PropertyAdminService } from '../../../core/services/property-admin.service';
import { AdminUpdatePropertyRequest, PropertyResponse } from '../../../core/models';
import { AppDialog } from '../../../shared/ui/dialog/app-dialog';
import { PROPERTY_TYPES, propertyTypeLabel } from '../property-labels';

/**
 * Modification des champs descriptifs d'un bien par un admin (PROPERTIES_UPDATE).
 * Les médias et le statut ne sont pas éditables ici : ils ont leurs propres flux.
 */
@Component({
  selector: 'app-property-edit-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, InputNumberModule,
    SelectModule, TextareaModule, ToggleSwitchModule, AppDialog],
  templateUrl: './property-edit-dialog.html',
  styleUrl: './property-edit-dialog.scss'
})
export class PropertyEditDialogComponent {
  private svc   = inject(PropertyAdminService);
  private fb    = inject(FormBuilder);
  private toast = inject(MessageService);

  visible  = model(false);
  property = input.required<PropertyResponse>();
  saved    = output<PropertyResponse>();

  saving = signal(false);

  readonly typeOptions = PROPERTY_TYPES.map(t => ({ label: propertyTypeLabel(t), value: t }));
  readonly transactionOptions = [{ label: 'Vente', value: 'VENTE' }, { label: 'Location', value: 'LOCATION' }];

  form = this.fb.group({
    title:           ['', [Validators.required, Validators.maxLength(255)]],
    description:     [''],
    type:            ['', Validators.required],
    transactionType: ['', Validators.required],
    price:           [0 as number | null, [Validators.required, Validators.min(0)]],
    surface:         [null as number | null, Validators.min(0)],
    nbPieces:        [null as number | null, Validators.min(0)],
    nbBedrooms:      [null as number | null, Validators.min(0)],
    nbBathrooms:     [null as number | null, Validators.min(0)],
    isFurnished:     [false],
    address:         [''],
    city:            ['', [Validators.required, Validators.maxLength(100)]],
    commune:         [''],
    features:        ['']
  });

  constructor() {
    // Réinitialise le formulaire à chaque ouverture, à partir du bien courant.
    effect(() => {
      if (!this.visible()) return;
      const p = this.property();
      this.form.reset({
        title: p.title, description: p.description ?? '', type: p.type, transactionType: p.transactionType,
        price: p.price, surface: p.surface ?? null, nbPieces: p.nbPieces ?? null,
        nbBedrooms: p.nbBedrooms ?? null, nbBathrooms: p.nbBathrooms ?? null,
        isFurnished: !!p.isFurnished, address: p.address ?? '', city: p.city, commune: p.commune ?? '',
        features: (p.features ?? []).join(', ')
      });
    });
  }

  submit() {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const v = this.form.getRawValue();
    // Le backend ignore les champs null (« inchangé ») : un nombre vidé n'est donc pas effacé.
    const changes: AdminUpdatePropertyRequest = {
      title: v.title!.trim(), description: v.description ?? '', type: v.type!, transactionType: v.transactionType!,
      price: v.price ?? 0, surface: v.surface, nbPieces: v.nbPieces, nbBedrooms: v.nbBedrooms,
      nbBathrooms: v.nbBathrooms, isFurnished: !!v.isFurnished, address: v.address ?? '',
      city: v.city!.trim(), commune: v.commune ?? '',
      features: (v.features ?? '').split(',').map(f => f.trim()).filter(Boolean)
    };

    this.saving.set(true);
    this.svc.updateProperty(this.property().id, changes).subscribe({
      next: updated => {
        this.saving.set(false);
        this.toast.add({ severity: 'success', summary: 'Bien modifié', life: 3500 });
        this.saved.emit(updated);
        this.visible.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La modification a échoué.', life: 5000 });
      }
    });
  }

  invalid(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && c.touched;
  }
}
